import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "../store";

import { errorHandler } from "app/shared/Service/errorHandler";
import { alertMessage } from "../actions/common";

import { getMealRatingApi, getRatingsApi, getRatingsReportApi } from "app/services/mealRating";

import { RatingCount, RatingDetails,MealRatingReportPayload } from "app/types/mealRating";
import { RootState } from "../reducers/hooks";
import { createQuery } from "app/utils/constants/common";
import { PaginationInterfaceLF, ReportInterace } from "app/types";

interface MealRatingFilters {
  fromDate: Date;
  toDate: Date;
}

interface InitialState {
  mealRatings: RatingDetails[] | null;
  mealRatingsPagination: PaginationInterfaceLF;
  ratings: RatingCount | null;
  isMealRatingsLoading: boolean;
  isRatingCountLoading: boolean;
  filters: MealRatingFilters | null;
  mealRatingsRowsPerPage: number;
  mealRatingReportLoading:boolean;
}

const initialState: InitialState = {
  mealRatings: null,
  mealRatingsPagination: null,
  ratings: null,
  isMealRatingsLoading: false,
  isRatingCountLoading: false,
  filters: null,
  mealRatingsRowsPerPage: 20,
  mealRatingReportLoading:false,
};

export const mealRatingSlice = createSlice({
  name: "mealRatings",
  initialState,
  reducers: {
    setMealRatings: (
      state,
      action: PayloadAction<{
        mealRatings: RatingDetails[];
        pagination: PaginationInterfaceLF;
      }>
    ) => {
      state.mealRatings = action.payload.mealRatings;
      state.mealRatingsPagination = action.payload.pagination;
    },
    setDishRatings: (state, action: PayloadAction<RatingCount | null>) => {
      state.ratings = action.payload;
    },
    setIsMealRatingsLoading: (state, action: PayloadAction<boolean>) => {
      state.isMealRatingsLoading = action.payload;
    },
    setIsMealRatingsReportLoading: (state, action: PayloadAction<boolean>) => {
      state.mealRatingReportLoading = action.payload;
    },
    setIsDishRatingsLoading: (state, action: PayloadAction<boolean>) => {
      state.isRatingCountLoading = action.payload;
    },
    setMealRatingFilters: (
      state,
      action: PayloadAction<MealRatingFilters | null>
    ) => {
      state.filters = action.payload;
    },
    setMealRatingsRowsPerPage: (state, action: PayloadAction<number>) => {
      state.mealRatingsRowsPerPage = action.payload;
    },
    clearMealRatings: (state) => {
      state.mealRatings = null;
      state.ratings = null;
    },
  },
});

export const {
  setMealRatings,
  setDishRatings,
  setIsMealRatingsLoading,
  setIsDishRatingsLoading,
  setMealRatingFilters,
  clearMealRatings,
  setMealRatingsRowsPerPage,
  setIsMealRatingsReportLoading
} = mealRatingSlice.actions;

export default mealRatingSlice.reducer;

export const getMealRatingsAction = ({
  storeId,
  dishId,
  continutationToken = null,
}: {
  storeId: number;
  dishId: number;
  continutationToken?: string | null;
}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setIsMealRatingsLoading(true));

    try {
      const { filters, mealRatingsRowsPerPage } = getState().mealRatings;

      const query =
        "?" +
        createQuery({
          filter: {
            ...filters,
            continutationToken,
            dishId: dishId,
            limit: mealRatingsRowsPerPage,
          },
        });

      const { data } = await getMealRatingApi(query, storeId);
      dispatch(setIsMealRatingsLoading(false));
      const pagination = data.pagination;
      const mealRatings = data.data;
      const includedClients = data.included?.clients || [];
      const clientNameMap = includedClients.reduce<Record<number, string>>(
        (acc, client) => {
          acc[client.id] = client.name;
          return acc;
        },
        {}
      );

      //  Add clientName to each meal rating
      const enrichedMealRatings = mealRatings.map((rating) => ({
        ...rating,
        clientName: clientNameMap[rating.clientId] || "__",
      }));

      dispatch(
        setMealRatings({ mealRatings: enrichedMealRatings, pagination })
      );

      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    } finally {
      dispatch(setIsMealRatingsLoading(false));
    }
  };
};

export const getDishRatingsAction = ({
  dishId,
  storeId,
}: {
  dishId: number;
  storeId: number;
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setIsDishRatingsLoading(true));
      const { data } = await getRatingsApi(dishId, storeId);
      dispatch(setIsDishRatingsLoading(false));

      dispatch(setDishRatings(data.data));
      return data;
    } catch (error) {
      dispatch(setIsDishRatingsLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};



export const downloadMealRatingReportAction = (
  storeId: number,
  payload: MealRatingReportPayload
) => {
  return async (dispatch: AppDispatch): Promise<ReportInterace> => {
    try {
      const query =
        "?" +
        createQuery({
          filter: payload,
        });
      dispatch(setIsMealRatingsReportLoading(true));
      const {
        data: { data },
      } = await getRatingsReportApi(query, storeId);
      dispatch(setIsMealRatingsReportLoading(false));
      return data;
    } catch (error) {
      dispatch(setIsMealRatingsReportLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};
